import {
  Button,
  Checkbox,
  Classes,
  HTMLTable,
  Intent,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';
import { alphaSort, isEqual, noop } from 'vet-bones/utils';
import { createSelectItem } from 'vet-bones/utils/select';

import { Link } from 'src/components/Link';
import { ItemQueryList } from 'src/components/select/ItemQueryList';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_SENSITIVE_PROPERTY_GROUP,
  TEST_IDS,
  getProjectDiagramEntityDrawerTableLinkTestId,
  getProjectDiagramEntityDrawerTableRowTestId,
} from 'src/constants/testIds';
import type { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useClickSensitivePropertyGroupLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickSensitivePropertyGroupLink';
import {
  useAddAttributeToSensitivePropertyGroup,
  useRemoveAttributeFromSensitivePropertyGroup,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useSensitivePropertyGroups';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { attributeDraftChangesSelector } from 'src/selectors/draftAttribute';
import { entityDrawerAttributeDataSelector } from 'src/selectors/entityDrawerAttributeData';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectSensitivePropertyGroupsByNameSelector } from 'src/selectors/projectSensitivePropertyGroupsByName';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const SpgQueryList = ItemQueryList.ofType<string>();

const groupNamePredicate = (query: string, groupName: string) =>
  groupName.toLowerCase().includes(query.toLowerCase());

export const AttributeDrawerSensitivePropertyGroups: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const attributeData = useReduxSelector(
    entityDrawerAttributeDataSelector(projectId),
    isEqual
  );
  const attributeIri = attributeData?.value || '';
  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );
  const sensitivePropertyGroupsByName = useReduxSelector(
    projectSensitivePropertyGroupsByNameSelector(projectId)
  );
  const { modifiedSensitivePropertyGroups } = useReduxSelector(
    attributeDraftChangesSelector(projectId, attributeIri),
    isEqual
  );

  const sensitivePropertyGroupNames = Object.keys(
    sensitivePropertyGroupsByName
  ).sort(alphaSort(([groupName]) => groupName));

  const attributeItem: AttributeTableItem = {
    value: attributeIri,
    text: getLabelForIri(labelsByIri, attributeIri),
    ranges: Object.values(attributeData?.ranges || {}).map((range) =>
      createSelectItem(range.value, range.value)
    ),
  };

  const getToolTipForSensitivePropertyGroup = React.useCallback(
    (groupName: string) => {
      const sensitivePropertyGroup = sensitivePropertyGroupsByName[groupName];

      const labels = sensitivePropertyGroup
        .filter((iri) => iri !== attributeIri)
        .map((attributeIri) => getLabelForIri(labelsByIri, attributeIri))
        .sort(alphaSort());

      if (labels.length < 3) {
        return '';
      }

      return labels.join(', ');
    },
    [attributeIri, labelsByIri, sensitivePropertyGroupsByName]
  );

  const getAttributeDisplayForSensitivePropertyGroup = React.useCallback(
    (attributeList: string[]) => {
      const filteredList = attributeList
        .filter((iri) => iri !== attributeIri)
        .sort(
          alphaSort((attributeIri) => getLabelForIri(labelsByIri, attributeIri))
        );

      const displayList = filteredList
        .slice(0, 2)
        .map((attributeIri) => getLabelForIri(labelsByIri, attributeIri))
        .join(', ');

      return { displayList, count: filteredList.length };
    },
    [attributeIri, labelsByIri]
  );

  const handleAddSensitivePropertyGroup = () => {
    dispatch(
      uiSlice.actions.toggledCreateSensitivePropertyGroupDrawer({
        attributeIri,
        isOpen: true,
      })
    );
  };

  const handleClickSensitivePropertyGroup =
    useClickSensitivePropertyGroupLink();

  const handleAddToGroup = useAddAttributeToSensitivePropertyGroup();

  const handleRemoveFromGroup = useRemoveAttributeFromSensitivePropertyGroup();

  return (
    <div className="sd-project-diagram-sensitive-property-groups">
      <SpgQueryList
        autofocus={false}
        customRenderer={({ itemList }, queryInputElement) => (
          <>
            <div className="sd-project-diagram-sensitive-property-groups__header">
              {
                copy.components.projectDiagram.attributeDrawer
                  .sensitivePropertyGroups
              }
              <div className="sd-project-diagram-sensitive-property-groups__header__flex">
                <div className="sd-project-diagram-sensitive-property-groups__header__flex">
                  <Button
                    className="sd-project-diagram-sensitive-property-groups__collapse-header__button"
                    data-testid={
                      TEST_IDS.projectDiagram.entityDrawer
                        .sensitivePropertyGroups.createButton
                    }
                    icon={IconNames.PLUS}
                    intent={Intent.PRIMARY}
                    minimal
                    onClick={handleAddSensitivePropertyGroup}
                    outlined
                    type="button"
                  >
                    {copy.components.projectDiagram.attributeDrawer.createSPG}
                  </Button>
                  {queryInputElement}
                </div>
              </div>
            </div>
            <HTMLTable
              className={classNames(
                'sd-project-diagram-sensitive-property-groups__table',
                Classes.COMPACT
              )}
            >
              <thead>
                <tr>
                  <th>{}</th>
                  <th>
                    {copy.components.projectDiagram.attributeDrawer.nameColumn}
                  </th>
                  <th>
                    {
                      copy.components.projectDiagram.attributeDrawer
                        .attributeColumn
                    }
                  </th>
                </tr>
              </thead>

              <tbody>{itemList}</tbody>
            </HTMLTable>
          </>
        )}
        inputProps={{
          className:
            'sd-project-diagram-sensitive-property-groups__collapse-header__search',
          leftIcon: IconNames.SEARCH,
          placeholder: copy.components.projectDiagram.attributeDrawer.filter,
          testId:
            TEST_IDS.projectDiagram.entityDrawer.sensitivePropertyGroups
              .filterInput,
        }}
        itemListRenderer={({ filteredItems, renderItem }) => (
          <>{filteredItems.map(renderItem)}</>
        )}
        itemPredicate={groupNamePredicate}
        itemRenderer={(groupName, { index }) => {
          const groupAttributes = sensitivePropertyGroupsByName[groupName];
          if (!groupAttributes) {
            return null;
          }

          const isAttributeInGroup = groupAttributes.includes(attributeIri);
          const { displayList, count } =
            getAttributeDisplayForSensitivePropertyGroup(groupAttributes);
          const previousGroupName =
            Number.isInteger(index) && (index as number) > 0
              ? sensitivePropertyGroupNames[(index as number) - 1]
              : undefined;
          return (
            <tr
              key={groupName}
              className={classNames({
                'sd-highlighted-table-row':
                  modifiedSensitivePropertyGroups.has(groupName),
                'sd-highlighted-table-row__no-top-border':
                  modifiedSensitivePropertyGroups.has(groupName) &&
                  previousGroupName &&
                  modifiedSensitivePropertyGroups.has(previousGroupName),
              })}
              data-testid={getProjectDiagramEntityDrawerTableRowTestId(
                groupName,
                PROJECT_DIAGRAM_ENTITY_DRAWER_SENSITIVE_PROPERTY_GROUP
              )}
            >
              <td>
                <Checkbox
                  checked={isAttributeInGroup}
                  data-testid={
                    TEST_IDS.projectDiagram.entityDrawer.sensitivePropertyGroups
                      .checkbox
                  }
                  onChange={() => {
                    if (isAttributeInGroup) {
                      handleRemoveFromGroup(groupName, attributeItem);
                    } else {
                      handleAddToGroup(groupName, attributeItem);
                    }
                  }}
                />
              </td>
              <td>
                <Link
                  onClick={() =>
                    handleClickSensitivePropertyGroup(
                      createSelectItem(groupName, groupName)
                    )
                  }
                  testId={getProjectDiagramEntityDrawerTableLinkTestId(
                    groupName,
                    PROJECT_DIAGRAM_ENTITY_DRAWER_SENSITIVE_PROPERTY_GROUP,
                    index
                  )}
                >
                  {groupName}
                </Link>
              </td>
              <td>
                <Text
                  data-testid={
                    TEST_IDS.projectDiagram.entityDrawer.sensitivePropertyGroups
                      .attributeList
                  }
                  title={getToolTipForSensitivePropertyGroup(groupName)}
                >
                  {displayList}
                  {copy.components.projectDiagram.attributeDrawer.moreCount(
                    count
                  )}
                </Text>
              </td>
            </tr>
          );
        }}
        items={sensitivePropertyGroupNames}
        noResults=""
        onSelect={noop}
      />
    </div>
  );
};
